import React, { useState, useEffect } from "react";
import { Top20Companies } from "../../assets/constants";
import { Link } from "react-router-dom";
import { FaSearch, FaFilter, FaBriefcase, FaClock, FaMapMarkerAlt, FaBuilding } from "react-icons/fa";
import { useTheme } from "../Theme/ThemeContext";

export default function JobFilters({
  locations,
  keyword,
  selectedCompany = "All",
  setKeyword,
  selectedType,
  setSelectedType,
  selectedExperience,
  setSelectedExperience,
  selectedLocation,
  setSelectedLocation,
}) {
  const [keywordInput, setKeywordInput] = useState(keyword);
  const { currentTheme } = useTheme();

  // Keep local input in sync if parent keyword changes
  useEffect(() => {
    setKeywordInput(keyword);
  }, [keyword]);

  return (
    <div className="glass rounded-2xl shadow-sm border border-white/20 dark:border-white/10 p-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <div className={`p-2 bg-gradient-to-br ${currentTheme.colors.gradient} rounded-lg`}>
          <FaFilter className="w-4 h-4 text-white" />
        </div>
        <h2 className="text-lg font-bold text-neutral-900 dark:text-white">Filters</h2>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
          <input
            type="text"
            placeholder="Search jobs by skill or company..."
            className="ca-input pl-11 text-sm"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setKeyword(keywordInput); // trigger search only on Enter
              }
            }}
          />
        </div>
      </div>

      <div className="space-y-6">
        {/* Job Type */}
        {selectedType && (
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
              <FaBriefcase className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />
              Job Type
            </label>
            <div className="flex flex-wrap gap-2">
              {["All", "job", "internship", "referral"].map((type) => (
                <button
                  key={type}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    selectedType === type 
                      ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-sm ${currentTheme.colors.glow}` 
                      : "bg-neutral-100/80 dark:bg-white/5 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200/80 dark:hover:bg-white/10"
                  }`}
                  onClick={() => setSelectedType(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Experience */}
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
            <FaClock className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />
            Experience
          </label>
          <div className="flex flex-wrap gap-2">
            {[
              "All",
              "Freshers",
              "0-2 years",
              "0-3 years",
              "3-5 years",
              "2-5 years",
              "5-7 years",
              "7+ years",
            ].map((exp) => (
              <button
                key={exp}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  selectedExperience === exp 
                    ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-sm ${currentTheme.colors.glow}` 
                    : "bg-neutral-100/80 dark:bg-white/5 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200/80 dark:hover:bg-white/10"
                }`}
                onClick={() => setSelectedExperience(exp)}
              >
                {exp}
              </button>
            ))}
          </div>
        </div>

        {/* <AmpAd /> */}

        {/* Location */}
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
            <FaMapMarkerAlt className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />
            Location
          </label>
          <div className="flex flex-wrap gap-2">
            {locations.map((loc) => (
              <button
                key={loc}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  selectedLocation === loc 
                    ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-sm ${currentTheme.colors.glow}` 
                    : "bg-neutral-100/80 dark:bg-white/5 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200/80 dark:hover:bg-white/10"
                }`}
                onClick={() => setSelectedLocation(loc)}
              >
                {loc}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
            <FaBuilding className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />
            Find Jobs by Companies
          </label>
          <div className="flex flex-wrap gap-2">
            {Top20Companies.map((company) => (
              <Link
                to={
                  company === "All"
                    ? "/jobs"
                    : `/jobs/company/${encodeURIComponent(company)}`
                }
                key={company}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  selectedCompany === company 
                    ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-sm ${currentTheme.colors.glow}` 
                    : "bg-neutral-100/80 dark:bg-white/5 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200/80 dark:hover:bg-white/10"
                }`}
              >
                {company}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
